import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * "You have unsaved changes" — for the settings forms, which are all
 * edit-then-Confirm and all one click away from a nav link.
 *
 * TWO EXITS, TWO MECHANISMS. Closing the tab or reloading is the browser's own
 * `beforeunload` prompt; we cannot word it and should not try. Clicking a link
 * inside the app never unloads anything, so that one is ours: while a form is
 * dirty this installs ONE capture-phase click listener and asks before letting
 * a link through.
 *
 * WHY NOT `useBlocker`: it needs a data router, and the app mounts a plain
 * `<BrowserRouter>` (App.tsx). Calling it here throws rather than degrades.
 * The cost of the click listener is that the BACK BUTTON is not covered — a
 * popstate has already happened by the time we hear about it, and the only way
 * to undo one is to push a decoy entry onto the history stack, which breaks
 * back for everyone who has nothing unsaved. Links are how this app is
 * navigated; back is the accepted gap.
 *
 * THE RULE THAT MATTERS MOST: it must never fire when nothing was typed. A
 * guard that cries wolf gets clicked through, and then it protects nothing.
 * That is why `dirty` is computed by each page as "the payload I would send
 * differs from the row the server gave me", not as "the user touched a field".
 */

/** What a click means, reduced to plain data so the decision can be tested. */
export interface NavigationAttempt {
  /** The anchor's resolved href, or null when the click missed every link. */
  href: string | null;
  /** `location.href` at the moment of the click. */
  currentUrl: string;
  /** The anchor's `target`, if any. */
  target?: string | null;
  /** The anchor carries `download`. */
  download?: boolean;
  /** Ctrl/Cmd/Shift/Alt held, or a non-primary mouse button. */
  modified?: boolean;
  /** Something upstream already handled this click. */
  defaultPrevented?: boolean;
}

/**
 * Whether this click is about to lose the edit, and so deserves the question.
 *
 * Everything it answers `false` to is a case where asking would be the wolf:
 * a modified click opens a new tab and leaves this page alone; a cross-origin
 * link unloads the document and gets `beforeunload` instead; and a link to a
 * different SECTION of the page we are already on — which is exactly what the
 * settings search does when the setting is on the current page — changes the
 * scroll position and nothing else.
 */
export function shouldConfirmNavigation(attempt: NavigationAttempt): boolean {
  const { href, currentUrl } = attempt;
  if (!href) return false;
  if (attempt.defaultPrevented || attempt.modified || attempt.download) return false;
  if (attempt.target && attempt.target !== '' && attempt.target !== '_self') return false;

  let destination: URL;
  let current: URL;
  try {
    destination = new URL(href, currentUrl);
    current = new URL(currentUrl);
  } catch {
    return false;
  }

  // Leaving the app entirely: the browser's own prompt covers it, and asking
  // twice trains the owner to dismiss both.
  if (destination.origin !== current.origin) return false;

  // Same page, hash or not — nothing is lost, so nothing is asked.
  if (destination.pathname === current.pathname && destination.search === current.search) {
    return false;
  }

  return true;
}

interface Guard {
  message: string;
}

/**
 * Module state, not a context: the listener has to be able to answer for ANY
 * dirty form, including one mounted in a part of the tree a provider around
 * Settings would not cover, and there is never a second copy of this app in
 * one document.
 */
const guards = new Map<symbol, Guard>();

/**
 * Asks whether to discard, for a navigation the click listener cannot see —
 * a `navigate()` call, such as opening a settings-search result.
 *
 * Returns true when it is safe to proceed (nothing dirty, or the owner said
 * discard). It does NOT clear the guard: the page that owns the edit unmounts
 * and unregisters itself.
 */
export function confirmDiscard(): boolean {
  const first = guards.values().next();
  if (first.done) return true;
  return window.confirm(first.value.message);
}

function handleCapturedClick(event: MouseEvent): void {
  const target = event.target;
  const anchor =
    target instanceof Element ? (target.closest('a[href]') as HTMLAnchorElement | null) : null;

  const shouldAsk = shouldConfirmNavigation({
    href: anchor?.getAttribute('href') ?? null,
    currentUrl: window.location.href,
    target: anchor?.getAttribute('target'),
    download: anchor?.hasAttribute('download') ?? false,
    modified:
      event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0,
    defaultPrevented: event.defaultPrevented,
  });
  if (!shouldAsk) return;

  if (confirmDiscard()) return;

  // Capture phase + stopPropagation: react-router's own listener never sees it,
  // so the route does not change behind the refused prompt.
  event.preventDefault();
  event.stopPropagation();
}

function handleBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  // Legacy spelling some browsers still require to show their prompt at all.
  event.returnValue = '';
}

function listen(): void {
  document.addEventListener('click', handleCapturedClick, true);
  window.addEventListener('beforeunload', handleBeforeUnload);
}

function stopListening(): void {
  document.removeEventListener('click', handleCapturedClick, true);
  window.removeEventListener('beforeunload', handleBeforeUnload);
}

/** Registers a dirty form. Returns the unregister, for an effect's cleanup. */
export function registerUnsavedGuard(message: string): () => void {
  const key = Symbol('unsaved-guard');
  if (guards.size === 0) listen();
  guards.set(key, { message });

  return () => {
    guards.delete(key);
    if (guards.size === 0) stopListening();
  };
}

/**
 * Registers `dirty` for the lifetime of the component.
 *
 * `dirty` false costs nothing — no listeners are installed at all until some
 * form actually has edits.
 */
export function useUnsavedGuard(dirty: boolean, message?: string): void {
  const { t } = useTranslation();
  const text = message ?? t('settings.unsaved.confirm');

  useEffect(() => {
    if (!dirty) return;
    return registerUnsavedGuard(text);
  }, [dirty, text]);
}
